/**
 * @fileoverview `<TotpInput>` — variante MFA de `<OtpInput>`.
 *
 * Utilisé pour Google Authenticator / applications TOTP compatibles. Ajoute
 * le contexte MFA et l'action optionnelle vers un code de récupération sans
 * changer le contrat canonique : six chiffres, uniquement.
 */

import { OtpInput, type OtpInputProps } from './otp-input.tsx';
import { cn } from '../lib/utils.ts';

export interface TotpInputProps extends Omit<OtpInputProps, 'length' | 'ariaLabel'> {
  /** Libellé du service affiché sous les cases. */
  issuerLabel?: string;
  /** Affiche un bouton texte pour basculer vers les codes de récupération. */
  recoveryActionLabel?: string;
  /** Handler appelé quand l'utilisateur veut utiliser un code de secours. */
  onRecoveryAction?: () => void;
}

/**
 * Champ MFA TOTP à six chiffres.
 *
 * @param props - Props MFA, héritées de `OtpInput` plus libellé émetteur et
 * action optionnelle vers les codes de récupération.
 * @returns Bloc de saisie TOTP à six chiffres avec aide contextuelle.
 *
 * @example
 * ```tsx
 * <TotpInput value={code} onChange={setCode} issuerLabel="Google Authenticator" />
 * ```
 */
export function TotpInput({
  issuerLabel = 'Google Authenticator ou application compatible TOTP',
  recoveryActionLabel,
  onRecoveryAction,
  className,
  ...props
}: TotpInputProps): React.JSX.Element {
  return (
    <div className={cn('space-y-2', className)}>
      <OtpInput
        {...props}
        length={6}
        ariaLabel="Code MFA à six chiffres"
        autoComplete="one-time-code"
      />
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <span className="text-muted-foreground">{issuerLabel}</span>
        {recoveryActionLabel && onRecoveryAction && (
          <button
            type="button"
            onClick={onRecoveryAction}
            className="text-quipux-blue-700 hover:text-quipux-blue-800 font-medium underline underline-offset-2"
          >
            {recoveryActionLabel}
          </button>
        )}
      </div>
    </div>
  );
}
